import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { PyroLayer, GROUND_Y, SHELL_SHAPES } from '../particle-layers/pyro-layer.js';
import { vdjColor, vdjBeatChroma, vdjCameraKick, VdjPost, VdjComfort, VDJ_DROP_FLASH } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. Les formes sont leur propre lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'shapes',
  family: 'geometric',
  danceability: 0.86,
  energyLevel: 0.80,
  bpmMin: 100,
  bpmMax: 160,
  movement: 0.80,
  tonality: 0.50,
  prefers: { gridStrength: [0.65, 1.0] },
  enabled: true
};

// Shapes : quatre formes en néon qui JOUENT LA MESURE - temps 1 cercle,
// temps 2 triangle, temps 3 carré, temps 4 étoile, et cette correspondance ne
// change jamais : après quelques mesures, la salle lit le rythme dans les
// formes. Derrière, des feux d'artifice ponctuent la structure (phrases,
// montées, drops), et sur les grands moments un bouquet ÉCRIT LE TITRE du
// morceau en étincelles. LOOK VDJ appliqué (lib/vdjLook.js).
//
// LE RENDU, dans une cible privée pleine résolution (demi-flottante, lue au
// texel près), pour deux raisons : les formes se dessinent EN ADDITIF par-
// dessus PyroLayer sans dépendre de la chaîne partagée, et la luminance de
// tout ce qui est à l'écran - bouquets compris - se mesure pour de vrai (la
// cible réduite en 16 x 16, relue deux fois par seconde) pour le gouverneur
// de confort.
//   1. fond noir (le clear)
//   2. PyroLayer, mode clip, avec son extension `title` (palette par
//      particule et bouquet-titre - voir pyro-layer.js)
//   3. un quad plein écran : les formes, en distances signées analytiques
//
// LES FORMES ont la même AIRE, pas le même rayon : pour un cercle de rayon
// R, le triangle équilatéral a un demi-côté de 1,3468 R, le carré un demi-
// côté de 0,8862 R, l'étoile (rayons 1 : 0,45) un rayon de 1,5413 R. L'étoile
// est la plus haute (2,788 R) : c'est elle qui fixe la taille des modes
// géants (SOLO : 70 % de la hauteur).
//
// LE CONTOUR est en PIXELS : d = |sdf| x pixels par unité - demi-largeur. Le
// coeur fait 3 à 6 px (bands.low), net au pixel près ; toute transformation
// de l'espace (zoom, échelle d'un anneau du tunnel) re-multiplie la distance
// par son échelle, donc la largeur ne bouge jamais.

const MODES = ['solo', 'row', 'wall', 'tunnel', 'morph', 'orbit'];
const MODE_ID = { solo: 0, row: 1, wall: 2, tunnel: 3, morph: 4, orbit: 5, concentric: 6 };
const NO_BPM_MODES = ['row', 'orbit'];
const AREA_K = [1.0, 1.3468, 0.8862, 1.5413];   // circle radius, tri half-side, box half-side, star radius
const STAR_TALL = 2.788;                        // star height / R
const SOLO_R = 0.70 * 2 / STAR_TALL;            // the tallest shape spans 70 % of the height
const MORPH_R = 0.55 * 2 / STAR_TALL;
const WALL_COLS = 16, WALL_ROWS = 9;
const TUNNEL_K = 1.6;                           // ring-to-ring scale
const SOLO_ZOOM = 0.15, SOLO_ZOOM_BEATS = 0.35, SOLO_FADE_BEATS = 0.55;
const DIM = 0.15;
const PYRO_UNDER_SHAPE = 0.60, PYRO_UNDER_WALL = 0.35;
const TITLE_SHAPES_GAIN = 0.5, PAUSE_GAIN = 0.3;
const TITLE_STARS = { 0: 400, 1: 700, 2: 1200 };
const TITLE_MAX_CHARS = 24;
const TITLE_BAND = [0.12, 0.30];                // from the top of the screen
const TITLE_MAX_WIDTH = 0.80;
const TITLE_MIN_INTERVAL_S = 60;
const NO_BPM = 120;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const DROP_WALL_BARS = 4;
const LUM_PERIOD_S = 0.5;
const WARM_WHITE = [1.0, 0.92, 0.80];

const TIER = {
  0: { pyroMax: 3000, trails: false, smoke: false, shellSlots: 3, wallShells: 3 },
  1: { pyroMax: 5000, trails: false, smoke: false, shellSlots: 5, wallShells: 5 },
  2: { pyroMax: 8000, trails: true, smoke: true, shellSlots: 6, wallShells: 5 }
};

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

const quadVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;

// --- the shapes -------------------------------------------------------------
const shapesFragmentShader = `
  precision mediump float;
  uniform vec2 uRes;              // target size in pixels
  uniform float uAspect;
  uniform float uMode;
  uniform float uShape;           // the beat's shape 0..3
  uniform float uNext;            // MORPH: the next one
  uniform float uMorph;           // MORPH: 0..1
  uniform float uRot;             // MORPH rotation
  uniform vec2 uCenter;           // the stage centre, R19 (clip units, x scaled by aspect)
  uniform vec3 uCol[4];           // colour of each shape
  uniform float uInt[4];          // ROW / ORBIT: intensity of each shape
  uniform vec2 uPos[4];           // ROW / ORBIT: where each shape is
  uniform float uSize;            // R for ROW / ORBIT
  uniform float uSoloScale;
  uniform float uSoloAmp;
  uniform float uTunnelT;         // 0..1, one ring per beat
  uniform float uWaveR;           // WALL: wave front, in cells
  uniform float uWaveAmp;
  uniform float uBeatLit;         // WALL: the beat's cells
  uniform float uKickZoom;
  uniform float uKickRoll;
  uniform float uCoreHalf;        // half the core width, px
  uniform float uHalo;            // 0 = core only (debug)
  uniform float uTwinkle;
  uniform float uTime;
  uniform float uGain;
  uniform vec3 uTitleMask;        // bottom, top, strength: the band the title is written in
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;

  // Inigo Quilez's 2D distance functions
  float sdTri(vec2 p, float r) {
    const float k = 1.7320508;
    p.x = abs(p.x) - r;
    p.y = p.y + r / k;
    if (p.x + k * p.y > 0.0) p = vec2(p.x - k * p.y, -k * p.x - p.y) / 2.0;
    p.x -= clamp(p.x, -2.0 * r, 0.0);
    return -length(p) * sign(p.y);
  }
  float sdBox(vec2 p, float b, float rr) {
    vec2 q = abs(p) - vec2(b - rr);
    return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - rr;
  }
  float sdStar5(vec2 p, float r, float rf) {
    const vec2 k1 = vec2(0.809016994375, -0.587785252292);
    const vec2 k2 = vec2(-0.809016994375, -0.587785252292);
    p.x = abs(p.x);
    p -= 2.0 * max(dot(k1, p), 0.0) * k1;
    p -= 2.0 * max(dot(k2, p), 0.0) * k2;
    p.x = abs(p.x);
    p.y -= r;
    vec2 ba = rf * vec2(-k1.y, k1.x) - vec2(0.0, 1.0);
    float h = clamp(dot(p, ba) / dot(ba, ba), 0.0, r);
    return length(p - ba * h) * sign(p.y * ba.x - p.x * ba.y);
  }
  // the same AREA for all four, R = the circle's radius
  float sdShape(vec2 p, float k, float R) {
    if (k < 0.5) return length(p) - R;
    if (k < 1.5) return sdTri(p, R * ${AREA_K[1]});
    if (k < 2.5) return sdBox(p, R * ${AREA_K[2]}, 0.02 * R * ${AREA_K[2]});
    return sdStar5(p, R * ${AREA_K[3]}, 0.45);
  }
  vec3 shapeCol(float k) {
    return k < 0.5 ? uCol[0] : k < 1.5 ? uCol[1] : k < 2.5 ? uCol[2] : uCol[3];
  }
  // the contour: a crisp warm-white core with the shape's colour around it
  vec3 contour(float sd, float k, float amp, float haloMul) {
    float dPx = abs(sd) * uRes.y * 0.5 - uCoreHalf;
    float core = 1.0 - smoothstep(-0.75, 0.75, dPx);
    float halo = exp(-max(dPx, 0.0) / 10.0) * 0.35 * uHalo * uTwinkle * haloMul;
    vec3 c = shapeCol(k);
    return (vec3(${WARM_WHITE.join(', ')}) * core + c * (core * 0.5 + halo)) * amp;
  }
  vec2 rot(vec2 p, float a) { float c = cos(a), s = sin(a); return vec2(c * p.x - s * p.y, s * p.x + c * p.y); }
  float safeAtt(vec2 uv) {
    vec2 ndc = uv * 2.0 - 1.0;
    vec2 s = vec2(ndc.x * uSafeAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(0.5, 1.0, smoothstep(0.0, uSafeFeather, d));
  }

  void main() {
    vec2 p = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    // the bar's camera kick, about the cover
    p = uCenter + rot(p - uCenter, uKickRoll) / uKickZoom;
    vec3 col = vec3(0.0);
#if MODE == 0   // SOLO
      vec2 q = (p - uCenter) / uSoloScale;
      col = contour(sdShape(q, uShape, ${SOLO_R.toFixed(4)}) * uSoloScale, uShape, uSoloAmp, 1.0);
    #elif MODE == 1 || MODE == 5   // ROW, ORBIT
      // the four never overlap: a pixel farther than a shape's bounding
      // radius plus its halo skips that shape (on the Pi, four full-screen
      // distance functions cost more than the whole firework layer)
      float reach = uSize * 1.6 + 70.0 / (uRes.y * 0.5);
      for (int i = 0; i < 4; i++) {
        vec2 d = p - uPos[i];
        if (dot(d, d) > reach * reach) continue;
        float k = float(i);
        col += contour(sdShape(d, k, uSize), k, uInt[i], 1.0);
      }
    #elif MODE == 2                            // WALL
      vec2 cellSize = vec2(2.0 * uAspect / ${WALL_COLS}.0, 2.0 / ${WALL_ROWS}.0);
      vec2 g = (p + vec2(uAspect, 1.0)) / cellSize;
      vec2 cell = floor(g);
      vec2 local = (fract(g) - 0.5) * cellSize;
      float k = mod(cell.x + 2.0 * cell.y, 4.0);
      vec2 dc = cell + 0.5 - vec2(${(WALL_COLS / 2).toFixed(1)}, ${(WALL_ROWS / 2).toFixed(1)});
      float r = length(dc * vec2(1.0, cellSize.y / cellSize.x));
      float wave = exp(-(r - uWaveR) * (r - uWaveR) / 1.2) * uWaveAmp;
      float lit = step(abs(k - uShape), 0.1) * uBeatLit;
      float amp = max(${DIM.toFixed(2)}, max(lit, wave * 0.5));
      float R = min(cellSize.x, cellSize.y) * 0.25;
      // the halo stays inside its cell: a 10 px halo around a 40 px shape
      // fills the cell, and the next cell's shape cuts it off in a hard edge
      vec2 e = abs(fract(g) - 0.5);
      float edge = smoothstep(0.5, 0.36, max(e.x, e.y));
      col = contour(sdShape(local, k, R), k, amp, 0.45 * edge);
    #elif MODE == 3                            // TUNNEL
      vec2 q = p - uCenter;
      float base = 0.12;
      float lv = log(max(length(q), 1e-3) / base) / log(${TUNNEL_K}) - uTunnelT;
      float j0 = floor(lv);
      for (int j = -1; j <= 1; j++) {
        float lev = j0 + float(j);
        float s = base * pow(${TUNNEL_K}, lev + uTunnelT);
        // rings are born small and dim, at full strength from mid-screen
        // born small and dim, brightest mid-screen, gone before they fill it:
        // the big rings are long and carried most of the light
        float amp = smoothstep(0.03, 0.22, s) * (1.0 - smoothstep(0.7, 1.6, s)) * 0.75;
        col += contour(sdShape(q / s, uShape, 1.0) * s, uShape, amp, 0.6);
      }
    #elif MODE == 4                            // MORPH
      vec2 q = rot(p - uCenter, uRot);
      float a = sdShape(q, uShape, ${MORPH_R.toFixed(4)});
      float b = sdShape(q, uNext, ${MORPH_R.toFixed(4)});
      float sd = mix(a, b, uMorph);
      vec3 ca = contour(sd, uShape, 1.0, 1.0), cb = contour(sd, uNext, 1.0, 1.0);
      col = mix(ca, cb, uMorph);
    #else                                             // DROP: four nested, white
      vec2 q = p - uCenter;
      for (int i = 0; i < 4; i++) {
        float k = float(i);
        float R = ${SOLO_R.toFixed(4)} * (1.0 - 0.2 * k);
        float dPx = abs(sdShape(q, k, R)) * uRes.y * 0.5 - uCoreHalf;
        float core = 1.0 - smoothstep(-0.75, 0.75, dPx);
        col += vec3(0.9) * (core + exp(-max(dPx, 0.0) / 10.0) * 0.15 * uHalo) * uSoloAmp;
      }
    #endif
    // while the title is written, the shapes step back from its band
    float band = smoothstep(uTitleMask.x - 0.08, uTitleMask.x, p.y) * (1.0 - smoothstep(uTitleMask.y, uTitleMask.y + 0.08, p.y));
    col *= 1.0 - uTitleMask.z * band;
    gl_FragColor = vec4(col * uGain * safeAtt(vUv), 1.0);
  }
`;

const displayFragmentShader = `
  precision mediump float;
  uniform sampler2D uTex;
  uniform float uGain;
  varying vec2 vUv;
  void main() { gl_FragColor = vec4(texture2D(uTex, vUv).rgb * uGain, 1.0); }
`;

// 16 x 16 luminance, each texel the mean of an 8 x 8 grid of taps over its
// cell (the source is read nearest: half-float is not filterable on the Pi)
const lumFragmentShader = `
  precision mediump float;
  uniform sampler2D uTex;
  uniform float uGain;
  varying vec2 vUv;
  void main() {
    vec2 cell = floor(vUv * 16.0);
    float s = 0.0;
    for (int y = 0; y < 8; y++) for (int x = 0; x < 8; x++) {
      vec2 uv = (cell + (vec2(float(x), float(y)) + 0.5) / 8.0) / 16.0;
      vec3 c = min(texture2D(uTex, uv).rgb * uGain, vec3(1.0));
      s += dot(c, vec3(0.299, 0.587, 0.114));
    }
    gl_FragColor = vec4(vec3(pow(s / 64.0, 1.0 / 2.2)), 1.0);
  }
`;

function hueDist(a, b) { const d = Math.abs(a - b) % 1; return Math.min(d, 1 - d); }

// Four colours as far apart as the cover allows: start from the most
// saturated, then greedily add the candidate whose nearest chosen hue is the
// farthest. A cover with fewer distinct hues is completed by quarter turns.
export function pickFour(palette) {
  const src = [];
  if (palette) for (const k of ['vividColors', 'dominantColors']) for (const c of (palette[k] || [])) src.push(c);
  const hsl = { h: 0, s: 0, l: 0 };
  const cand = src.map((c) => { new THREE.Color(c).getHSL(hsl); return { c, h: hsl.h, s: hsl.s }; });
  const chosen = [];
  if (cand.length) {
    cand.sort((a, b) => b.s - a.s);
    chosen.push(cand[0]);
    while (chosen.length < 4) {
      let best = null, bestD = -1;
      for (const k of cand) {
        if (chosen.includes(k)) continue;
        const d = Math.min(...chosen.map((x) => hueDist(x.h, k.h)));
        if (d > bestD) { bestD = d; best = k; }
      }
      if (!best || bestD < 0.06) break;
      chosen.push(best);
    }
  }
  const h0 = chosen.length ? chosen[0].h : 0.83;
  while (chosen.length < 4) {
    // complete with the hue farthest from everything chosen, in 1/8 steps
    let bh = 0, bd = -1;
    for (let k = 0; k < 8; k++) {
      const h = (h0 + k / 8) % 1;
      const d = Math.min(...chosen.map((x) => hueDist(x.h, h)), 1);
      if (d > bd) { bd = d; bh = h; }
    }
    chosen.push({ c: new THREE.Color().setHSL(bh, 1, 0.55).getHex(), h: bh, s: 1 });
  }
  return chosen.map((x) => vdjColor(x.c));
}

// The title, prepared ONCE per track: rendered into an off-screen canvas,
// sampled on a jittered grid, mapped into the band at the top of the frame.
export function titleText(raw) {
  const t = String(raw || '').trim().toUpperCase().replace(/\s+/g, ' ');
  if (!t) return '';
  if (t.length <= TITLE_MAX_CHARS) return t;
  const cut = t.slice(0, TITLE_MAX_CHARS - 1);
  const sp = cut.lastIndexOf(' ');
  return (sp > 6 ? cut.slice(0, sp) : cut).replace(/[\s,.;:-]+$/, '') + '…';
}

function sampleTitle(text, count, band, maxHalfW, seedIn) {
  if (typeof document === 'undefined' || !text) return null;
  const FONT = 120;
  const cv = document.createElement('canvas');
  const ctx = cv.getContext('2d', { willReadFrequently: true });
  const font = `900 ${FONT}px "Helvetica Neue", Helvetica, Arial, sans-serif`;
  ctx.font = font;
  const w = Math.ceil(ctx.measureText(text).width) + 40;
  const h = Math.ceil(FONT * 1.4);
  cv.width = w; cv.height = h;
  ctx.font = font;
  ctx.fillStyle = '#fff';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, 20, h / 2);
  const img = ctx.getImageData(0, 0, w, h).data;
  // the ink's own box, so the band is filled by letters and not by padding
  let x0 = w, x1 = 0, y0 = h, y1 = 0, filled = 0;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (img[(y * w + x) * 4 + 3] > 127) { filled++; if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
  }
  if (!filled) return null;
  let seed = seedIn || 7;
  const rnd = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
  // a jittered grid fine enough to give at least `count` stars on ink
  let step = Math.sqrt(filled / count) * 1.05;
  let pts = [];
  for (let tries = 0; tries < 12; tries++) {
    pts = [];
    for (let gy = y0; gy <= y1; gy += step) for (let gx = x0; gx <= x1; gx += step) {
      const x = Math.floor(gx + rnd() * step * 0.85), y = Math.floor(gy + rnd() * step * 0.85);
      if (x > x1 || y > y1) continue;
      if (img[(y * w + x) * 4 + 3] > 127) pts.push(x, y);
    }
    if (pts.length / 2 >= count) break;
    step *= 0.9;
  }
  // exactly `count`, spread evenly over the grid order (not a random draw)
  const have = pts.length / 2;
  const n = Math.min(count, have);
  const inkW = x1 - x0 + 1, inkH = y1 - y0 + 1;
  const bandH = band[0] - band[1];
  const scale = Math.min(bandH / inkH, (2 * maxHalfW) / inkW);
  const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2, yc = (band[0] + band[1]) / 2;
  const out = new Float32Array(count * 2);
  for (let i = 0; i < n; i++) {
    const j = Math.floor(i * have / n);
    out[i * 2] = (pts[j * 2] - cx) * scale;
    out[i * 2 + 1] = yc - (pts[j * 2 + 1] - cy) * scale;
  }
  return { xy: out, count: n, width: inkW * scale, height: inkH * scale, top: yc + inkH * scale / 2, bottom: yc - inkH * scale / 2 };
}

const SHARED = { uniforms: null, variants: null };

export class ShapesVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER[this._tier] || TIER[1];
    const m = urlParam('shapesmode');
    this._forceMode = m && MODES.includes(m) ? m : null;
    const b = parseInt(urlParam('shapesbeat'), 10);
    this._forceBeat = b >= 1 && b <= 4 ? b - 1 : null;
    this._coreOnly = urlParam('shapescore') === '1';
    this._forceTitle = urlParam('shapestitle') === '1';
    this._forceTitleText = urlParam('shapestitletext');
    this._post = new VdjPost({ bloomStrength: 0.42, bloomThreshold: 0.80, grain: 0.012, vignette: 0.45, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort();
  }

  get postProcessing() { return this._post.value; }

  init(scene, camera) {
    this._scene = scene;
    const L = this._layout;
    this._seed = 97;
    this._time = 0;

    // the private scene: PyroLayer, then the shapes on top
    this._priv = new THREE.Scene();
    // spans the simulation's own units, so PyroLayer's trails (LineMaterial,
    // the one part that goes through a camera) land where the points are
    this._ortho = new THREE.OrthographicCamera(-1.78, 1.78, 1, -1, 0, 10);
    this._ortho.position.z = 1;
    this._pyro = new PyroLayer({ max: L.pyroMax, trails: L.trails, smoke: L.smoke, reflection: false, crackle: true, flames: false,
                                 linewidth: 1.4, space: 'clip', title: TITLE_STARS[this._tier] || 700, shellSlots: L.shellSlots });
    this._pyro.init(this._priv);

    // ONE UNIFORMS OBJECT for every mode's variant; the variants differ
    // only by the MODE define (see _variant)
    if (!SHARED.uniforms) {
      SHARED.uniforms = {
          uRes: { value: new THREE.Vector2(1280, 720) }, uAspect: { value: 1.78 }, uMode: { value: 1 },
          uShape: { value: 0 }, uNext: { value: 1 }, uMorph: { value: 0 }, uRot: { value: 0 },
          uCenter: { value: new THREE.Vector2(0, 0) },
          uCol: { value: [new THREE.Color(), new THREE.Color(), new THREE.Color(), new THREE.Color()] },
          uInt: { value: [0, 0, 0, 0] },
          uPos: { value: [new THREE.Vector2(), new THREE.Vector2(), new THREE.Vector2(), new THREE.Vector2()] },
          uSize: { value: 0.2 }, uSoloScale: { value: 1 }, uSoloAmp: { value: 0 }, uTunnelT: { value: 0 },
          uWaveR: { value: 0 }, uWaveAmp: { value: 0 }, uBeatLit: { value: 0 },
          uKickZoom: { value: 1 }, uKickRoll: { value: 0 }, uCoreHalf: { value: 2.25 }, uHalo: { value: 1 },
          uTwinkle: { value: 1 }, uTime: { value: 0 }, uGain: { value: 1 }, uTitleMask: { value: new THREE.Vector3(2, 2, 0) },
          uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
          uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
      };
      SHARED.variants = new Map();
    }
    this._u = SHARED.uniforms;
    this._mat = this._variant(MODE_ID.row);
    this._u.uHalo.value = this._coreOnly ? 0 : 1;
    this._shapesQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._mat);
    this._shapesQuad.frustumCulled = false; this._shapesQuad.renderOrder = 1000;
    this._priv.add(this._shapesQuad);

    // the private target: full resolution, half-float so additive light
    // above 1 survives to the bloom, read 1:1 (nearest) by the display quad
    this._buffer = new FeedbackBuffer({ scale: 1, single: true, type: THREE.HalfFloatType, filter: THREE.NearestFilter });
    this._dispU = { uTex: { value: null }, uGain: { value: 1 } };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._dispU, vertexShader: quadVertexShader, fragmentShader: displayFragmentShader, depthTest: false, depthWrite: false }));
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._display);
    this._lumTarget = new THREE.WebGLRenderTarget(16, 16, { depthBuffer: false, stencilBuffer: false });
    this._lumPixels = new Uint8Array(16 * 16 * 4);
    this._lumU = { uTex: { value: null }, uGain: { value: 1 } };
    this._lumScene = new THREE.Scene();
    const lq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._lumU, vertexShader: quadVertexShader, fragmentShader: lumFragmentShader, depthTest: false, depthWrite: false }));
    lq.frustumCulled = false;
    this._lumScene.add(lq);
    this._lumAge = 0; this._lumMeasured = 0.1; this._lumPending = false;
    this._size = new THREE.Vector2();

    // musical state
    this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null;
    this._free = 0; this._freeBar = 0;
    this._beats = 0; this._bars = 0; this._phrases = 0;
    this._secondsBeat = 0.5; this._hasGrid = false;
    this._shapeIdx = 0; this._shapeAge = 10; this._lastSub = -1;
    this._modeTurn = 1; this._mode = 'row'; this._dropWallUntil = -1; this._dropWhiteUntil = -1;
    this._waveAge = 10; this._beatAge = 10;
    this._assign = [0, 1, 2, 3];
    this._colors = pickFour(null);
    this._paletteSeen = undefined;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._dropCount = 0; this._armed = false; this._cometSide = 1;
    this._buildup = 0; this._buildStage = 0;
    this._titleSeen = undefined; this._title = null; this._titleBox = null; this._lastTitleAt = -1e9;
    this._noBpmBars = 0;
    this._log = []; this._bpNow = 0;
    this._applyColors();
  }

  // A compiled variant per mode: on the Pi's GPU a mode switch on a uniform
  // still paid for every mode's code on every pixel. Cached across
  // instances; all variants are compiled at the first render (_render).
  _variant(id) {
    let m = SHARED.variants.get(id);
    if (!m) {
      m = new THREE.ShaderMaterial({
        uniforms: SHARED.uniforms, defines: { MODE: id },
        vertexShader: quadVertexShader, fragmentShader: shapesFragmentShader,
        transparent: true, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false
      });
      SHARED.variants.set(id, m);
    }
    return m;
  }

  _random() { this._seed = (this._seed * 1664525 + 1013904223) >>> 0; return this._seed / 4294967296; }

  // --- palette ----------------------------------------------------------------
  _applyPalette(profile) {
    if (profile.artworkPalette === this._paletteSeen) return;
    this._paletteSeen = profile.artworkPalette;
    this._colors = pickFour(profile.artworkPalette);
    // the fireworks' four slots are the four colours in a FIXED order; a
    // shape's slot is its colour's index, so a bouquet keeps its colour
    this._pyro.setPalette(this._colors);
    this._applyColors();
  }
  _applyColors() {
    for (let k = 0; k < 4; k++) this._u.uCol.value[k].copy(this._colors[this._assign[k]]);
    if (this._pyro) this._pyro.setPalette(this._colors);
  }
  _slotOf(shape) { return this._assign[shape]; }

  // --- the title ----------------------------------------------------------------
  _safeTop() {
    // R19: the stage block's top, not the cover's
    return safeZoneUniforms.uStageCenter.value.y + safeZoneUniforms.uStageHalfSize.value.y;
  }
  _titleBand() {
    let top = 1 - 2 * TITLE_BAND[0], bottom = 1 - 2 * TITLE_BAND[1];
    // never into the safe zone: the cover, and the DOM title under it
    const floor = this._safeTop() + 0.05;
    if (bottom < floor) bottom = floor;
    if (top - bottom < 0.12) top = Math.min(0.90, bottom + 0.12);
    if (top - bottom < 0.08) return null;
    return [top, bottom];
  }
  _prepareTitle(profile) {
    const raw = this._forceTitleText != null ? this._forceTitleText : profile.title;
    const text = titleText(raw);
    const band = this._titleBand();
    const key = text + '|' + (band ? band.join(',') : 'x') + '|' + (safeZoneUniforms.uAspect.value || 1.78).toFixed(3);
    if (key === this._titleSeen) return;
    this._titleSeen = key;
    this._title = null;
    if (!text || !band) return;                      // no metadata: the title gesture is off
    const aspect = safeZoneUniforms.uAspect.value || 1.78;
    const s = sampleTitle(text, TITLE_STARS[this._tier] || 700, band, aspect * TITLE_MAX_WIDTH, 11);
    if (!s) return;
    this._pyro.setTitleTargets(s.xy, s.count);
    this._title = { text, count: s.count, band };
    this._titleBox = s;
  }
  _fireTitle(slot) {
    if (!this._title || this._pyro.titleBusy) return false;
    const b = this._title.band;
    this._pyro.setSlot(slot);
    const ok = this._pyro.fireTitle(0, 0, (b[0] + b[1]) / 2, 1.5 * 4 * this._secondsBeat);
    if (ok) this._lastTitleAt = this._time;
    return ok;
  }

  // --- drops ------------------------------------------------------------------
  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0;
      while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  // --- fireworks ----------------------------------------------------------------
  // A nozzle on the floor, outside the cover's columns, on the given side.
  _nozzle(side, frac) {
    const aspect = safeZoneUniforms.uAspect.value || 1.78;
    const c = safeZoneUniforms.uStageCenter.value, hs = safeZoneUniforms.uStageHalfSize.value;   // R19: stage, not cover
    const inner = Math.min(aspect - 0.25, Math.abs(c.x) + hs.x + 0.30);
    const outer = aspect * 0.92;
    const x = side * (inner + (outer - inner) * (frac != null ? frac : this._random()));
    return { x, y: GROUND_Y, tilt: 0, dx: 0, dy: 1, edge: 'floor' };
  }
  // A break point in the top 55 % of the frame, clear of the safe zone.
  _apex() {
    return -0.1 + 0.15 + this._random() * 0.55;
  }
  // what fired, when, and on which musical event (read by the probes)
  _note(what, why) {
    this._log.push({ t: +this._time.toFixed(3), what, why, beat: this._beats, bar: this._bars, bp: +(this._bpNow || 0).toFixed(3) });
    if (this._log.length > 300) this._log.shift();
  }
  _comet(shape) {
    this._cometSide = -this._cometSide;
    this._pyro.setSlot(this._slotOf(shape));
    this._pyro.fireComet(this._nozzle(this._cometSide));
  }
  _shell(shape, side, frac, form) {
    this._pyro.setSlot(this._slotOf(shape));
    this._pyro.fireShell(this._nozzle(side, frac), form != null ? form : (this._random() * SHELL_SHAPES.length) | 0, this._apex());
  }
  _wallOfFire(shape) {
    const n = this._layout.wallShells;
    for (let i = 0; i < n; i++) {
      const side = i % 2 === 0 ? -1 : 1;
      const k = Math.floor(i / 2), per = Math.ceil(n / 2);
      this._shell(shape, side, (k + 0.5) / per, 0);
    }
  }

  onTrackChange(profile) {
    this._applyPalette(profile);
    this._prepareTitle(profile);
    this._dropIdx = 0; this._lastNowMs = null; this._dropCount = 0;
    this._armed = false; this._phrases = 0;
  }

  // --- the frame --------------------------------------------------------------
  update(deltaTime, profile) {
    const dt = Math.min(deltaTime, 0.1);
    this._applyPalette(profile);
    this._prepareTitle(profile);                       // no-op unless the title or the frame changed
    const paused = profile.playbackState !== 'PLAYING';
    const U = this._u;
    const aspect = safeZoneUniforms.uAspect.value || 1.78;
    U.uAspect.value = aspect;
    const sc = safeZoneUniforms.uStageCenter.value;   // R19: kick and SOLO about the stage centre
    U.uCenter.value.set(sc.x, sc.y);
    if (paused) {
      // ROW at 30 %, fireworks frozen in the air
      this._layoutRow(aspect);
      for (let k = 0; k < 4; k++) U.uInt.value[k] = 0.35;
      U.uMode.value = MODE_ID.row; U.uGain.value = PAUSE_GAIN;
      this._post.update(1);
      return;
    }
    this._time += dt;
    const director = profile.director;
    const bands = (director && director.hasReliableGrid && director.bands) ? director.bands : null;
    const low = bands ? clamp01(bands.low) : 0.5, high = bands ? clamp01(bands.high) : 0.4;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    const bp = profile.beatPhase, barPhaseIn = profile.barPhase, ph = profile.phrasePhase;
    this._hasGrid = bp != null && bpm > 0;
    this._secondsBeat = this._hasGrid ? 60 / bpm : 60 / NO_BPM;
    const beatS = this._secondsBeat, barS = 4 * beatS;

    // beat, bar, phrase edges - from the grid, or a 120 BPM clock
    let beat = false, bar = false, phrase = false;
    if (this._hasGrid) {
      if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) beat = true;
      this._phaseBeat = bp;
      if (barPhaseIn != null) { if (this._phaseBar != null && barPhaseIn < this._phaseBar - 0.5) bar = true; this._phaseBar = barPhaseIn; }
      else if (beat && (this._beats + 1) % 4 === 0) bar = true;
      if (ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true; this._phasePhrase = ph; }
      else if (bar && (this._bars + 1) % 4 === 0) phrase = true;
    } else {
      this._free += dt;
      if (this._free >= beatS) { this._free -= beatS; beat = true; }
      if (beat) { this._freeBar = (this._freeBar + 1) % 4; if (this._freeBar === 0) bar = true; }
      if (bar && (this._bars + 1) % 4 === 0) phrase = true;
    }
    if (beat) this._beats++;
    if (bar) this._bars++;
    if (phrase) this._phrases++;
    const beatPhase = this._hasGrid ? bp : this._free / beatS;
    const barPhase = this._hasGrid && barPhaseIn != null ? barPhaseIn : (this._freeBar + beatPhase) / 4;
    const beatInBar = Math.min(3, Math.floor(barPhase * 4));
    this._bpNow = beatPhase;

    // the build: a known drop approaching, or the director's build segment
    const next = director && director.nextEvent;
    let stage = 0;
    if (this._hasGrid && next && next.type === 'drop' && next.msUntil < 8000) stage = next.msUntil < 2 * barS * 1000 ? 2 : 1;
    if (this._hasGrid && director && director.mode === 'build' && stage === 0) stage = 1;
    this._buildStage = stage;

    // THE SHAPE OF THE BEAT. In the build the correspondence runs at the
    // half-beat, then at the quarter-beat - the same order, faster.
    const rate = stage === 2 ? 4 : stage === 1 ? 2 : 1;
    const sub = Math.floor(barPhase * 4 * rate);
    let shapeIdx = this._forceBeat != null ? this._forceBeat : sub % 4;
    const subEdge = sub !== this._lastSub;
    this._lastSub = sub;
    if (subEdge) this._shapeAge = 0;
    this._shapeAge += dt;
    this._shapeIdx = shapeIdx;
    if (beat) this._beatAge = 0;
    this._beatAge += dt;

    // DROP
    const drop = this._hasGrid && this._tickDrops(profile);
    if (drop) {
      this._dropCount++;
      this._armed = true;
      if (director && this._comfort.requestFlash(this._time)) director.flash = VDJ_DROP_FLASH;
      this._dropWhiteUntil = this._time + beatS;
      this._dropWallUntil = this._time + DROP_WALL_BARS * barS;
      this._wallOfFire(beatInBar); this._note('wall', 'drop');
      // the title: the first drop, then every other one, never twice a minute
      if ((this._dropCount - 1) % 2 === 0 && this._time - this._lastTitleAt >= TITLE_MIN_INTERVAL_S && this._fireTitle(this._slotOf(beatInBar))) this._note('title', 'drop');
    }

    // PHRASE: next mode, new colours, a bouquet
    if (phrase) {
      const list = this._hasGrid ? MODES : NO_BPM_MODES;
      this._modeTurn = (this._modeTurn + 1) % list.length;
      const a = this._assign; a.push(a.shift());      // a new deal of the colours
      this._applyColors();
      const analysed = this._drops.length > 0;
      if (!analysed && this._phrases >= 2) this._armed = true;
      if (this._armed && this._hasGrid) { this._shell(0, this._random() < 0.5 ? -1 : 1, null, 0); this._note('shell', 'phrase'); }
      // without analysis: the title at the third phrase, then every 8
      if (!analysed && (this._phrases === 3 || (this._phrases > 3 && (this._phrases - 3) % 8 === 0))
          && this._time - this._lastTitleAt >= TITLE_MIN_INTERVAL_S && this._fireTitle(this._slotOf(0))) this._note('title', 'phrase');
    }
    // BAR: comet every 2 bars (every bar in a build, every beat in its last 2)
    if (this._hasGrid) {
      if (stage === 2) { if (beat) { this._comet(beatInBar); this._note('comet', 'build-beat'); } }
      else if (bar && (stage === 1 || this._bars % 2 === 0)) { this._comet(beatInBar); this._note('comet', stage === 1 ? 'build-bar' : 'bar'); }
    } else if (bar) {
      this._noBpmBars++;
      if (this._noBpmBars % 8 === 0) { this._shell(0, this._random() < 0.5 ? -1 : 1, null, 0); this._note('shell', 'nobpm-8bars'); }
    }
    if (this._forceTitle && !this._pyro.titleBusy && this._time - this._lastTitleAt > 2 && this._time > 1) this._fireTitle(this._slotOf(0));

    // the mode
    const list = this._hasGrid ? MODES : NO_BPM_MODES;
    let mode = this._forceMode || list[this._modeTurn % list.length];
    const inDropWall = this._time < this._dropWallUntil && !this._forceMode;
    if (inDropWall) mode = 'wall';
    this._mode = mode;
    const white = this._time < this._dropWhiteUntil && !this._forceMode;

    // the bar's camera kick, the beat's RGB split
    const kick = vdjCameraKick(barPhase, this._bars, 4);
    U.uKickZoom.value = kick.zoom; U.uKickRoll.value = kick.roll;
    this._post.update(vdjBeatChroma(beatPhase, beatS));

    U.uShape.value = shapeIdx;
    U.uCoreHalf.value = 1.5 + 1.5 * low;               // 3 to 6 px
    U.uTwinkle.value = 1 + (high - 0.4) * 0.5 * (Math.sin(this._time * 37.0) * 0.5 + Math.sin(this._time * 23.0) * 0.5);
    U.uTime.value = this._time;
    const ageB = this._shapeAge / (beatS / rate);      // in units of the current step
    let peak = 0;
    if (white) {
      U.uMode.value = MODE_ID.concentric;
      U.uSoloAmp.value = 1; peak = 1;
    } else if (mode === 'solo') {
      U.uMode.value = MODE_ID.solo;
      const z = clamp01(ageB / SOLO_ZOOM_BEATS);
      U.uSoloScale.value = 1 + SOLO_ZOOM * Math.pow(1 - z, 3);
      U.uSoloAmp.value = Math.exp(-ageB / SOLO_FADE_BEATS) * 0.9 + 0.1;
      peak = U.uSoloAmp.value;
    } else if (mode === 'row') {
      U.uMode.value = MODE_ID.row;
      this._layoutRow(aspect);
      for (let k = 0; k < 4; k++) U.uInt.value[k] = k === shapeIdx ? 1 : DIM;
      peak = 1;
    } else if (mode === 'wall') {
      U.uMode.value = MODE_ID.wall;
      const wb = beatPhase;                            // a wave per beat, out in half a beat
      U.uWaveR.value = clamp01(wb / 0.5) * 10.5;
      U.uWaveAmp.value = wb < 0.5 ? 1 : Math.exp(-(wb - 0.5) * 6);
      U.uBeatLit.value = 0.35 + 0.65 * Math.exp(-ageB / 0.6);
      peak = 0.5;
    } else if (mode === 'tunnel') {
      U.uMode.value = MODE_ID.tunnel;
      U.uTunnelT.value = beatPhase;
      peak = 0.8;
    } else if (mode === 'morph') {
      U.uMode.value = MODE_ID.morph;
      U.uNext.value = (shapeIdx + 1) % 4;
      const k = clamp01((beatPhase - 0.5) / 0.5);
      U.uMorph.value = this._forceBeat != null ? 0 : k * k * (3 - 2 * k);
      U.uRot.value = -(this._bars + barPhase) * Math.PI / 2;
      peak = 1;
    } else {
      U.uMode.value = MODE_ID.orbit;
      this._layoutOrbit(aspect, barPhase);
      peak = 1;
    }

    // the title's moment: shapes at 50 %
    const titleOn = this._pyro.titleBusy && this._pyro.titlePhase !== 'rise';
    U.uGain.value = titleOn ? TITLE_SHAPES_GAIN : 1;
    if (titleOn && this._titleBox) U.uTitleMask.value.set(this._titleBox.bottom, this._titleBox.top, 0.8);
    else U.uTitleMask.value.z = 0;
    // fireworks dimmed under a shape at full intensity, and under the wall
    const pyroGain = mode === 'wall' ? PYRO_UNDER_WALL : 1 - PYRO_UNDER_SHAPE * clamp01(peak) * U.uGain.value;
    this._pyro.setIntensity(pyroGain, 1);
    this._pyro.update(dt, high, profile.progress || 0);

    // comfort: the measured mean luminance of everything drawn
    this._lumAge += dt;
    if (this._lumAge >= LUM_PERIOD_S) { this._lumAge = 0; this._lumPending = true; }
    this._dispU.uGain.value = this._comfort.measure(this._lumMeasured, dt);
  }

  // ROW: two on each side of the cover, beat order left to right
  _layoutRow(aspect) {
    const U = this._u;
    const c = safeZoneUniforms.uStageCenter.value, hs = safeZoneUniforms.uStageHalfSize.value;   // R19
    const left = -aspect, right = aspect;
    const inL = c.x - hs.x - 0.06, inR = c.x + hs.x + 0.06;
    const wL = (inL - left) / 2, wR = (right - inR) / 2;
    const w = Math.max(0.08, Math.min(wL, wR));
    const R = Math.min(w / 3.6, 0.6 / STAR_TALL * 2);
    U.uSize.value = R;
    const y = c.y;
    U.uPos.value[0].set(left + wL * 0.5, y);
    U.uPos.value[1].set(left + wL * 1.5, y);
    U.uPos.value[2].set(inR + wR * 0.5, y);
    U.uPos.value[3].set(inR + wR * 1.5, y);
  }
  // ORBIT: one revolution per bar; the beat's shape passes the top on its beat
  _layoutOrbit(aspect, barPhase) {
    const U = this._u;
    const c = safeZoneUniforms.uStageCenter.value, hs = safeZoneUniforms.uStageHalfSize.value;   // R19
    const R = Math.min(0.17, (aspect - hs.x) / 6);
    U.uSize.value = R;
    const rx = Math.min(aspect - R * 1.7, hs.x + R * 2.2 + 0.1);
    const ry = Math.min(0.98 - c.y - R * 1.5, hs.y + R * 1.2);
    for (let k = 0; k < 4; k++) {
      const a = Math.PI / 2 - 2 * Math.PI * (barPhase - k / 4);
      U.uPos.value[k].set(c.x + rx * Math.cos(a), c.y + ry * Math.sin(a));
      let d = (barPhase - k / 4) % 1; if (d < 0) d += 1; d = Math.min(d, 1 - d) * 4;  // beats from the top
      U.uInt.value[k] = 0.45 + 0.85 * Math.exp(-(d / 0.18) * (d / 0.18));
    }
  }

  _render(renderer) {
    const prev = renderer.getRenderTarget();
    this._buffer.ensureSize(renderer);
    const rt = this._buffer.read;
    const aspect = rt.width / rt.height;
    if (Math.abs(this._ortho.right - aspect) > 1e-4) {
      this._ortho.left = -aspect; this._ortho.right = aspect; this._ortho.updateProjectionMatrix();
    }
    this._pyro.setResolution(rt.width, rt.height);
    this._u.uRes.value.set(rt.width, rt.height);
    this._shapesQuad.material = this._variant(this._u.uMode.value);
    if (!SHARED.compiled) {
      // every mode's variant compiled now, once: a phrase must not hitch
      SHARED.compiled = true;
      const keep = this._shapesQuad.material;
      for (const id of Object.values(MODE_ID)) { this._shapesQuad.material = this._variant(id); renderer.compile(this._priv, this._ortho); }
      this._shapesQuad.material = keep;
    }
    const clear = renderer.getClearAlpha(), cc = new THREE.Color(); renderer.getClearColor(cc);
    renderer.setRenderTarget(rt);
    renderer.setClearColor(0x000000, 1);
    renderer.clear(true, false, false);
    renderer.render(this._priv, this._ortho);
    renderer.setClearColor(cc, clear);
    if (this._lumPending) {
      this._lumPending = false;
      this._lumU.uTex.value = rt.texture;
      this._lumU.uGain.value = this._dispU.uGain.value;
      renderer.setRenderTarget(this._lumTarget);
      renderer.render(this._lumScene, this._ortho);
      renderer.readRenderTargetPixels(this._lumTarget, 0, 0, 16, 16, this._lumPixels);
      let s = 0;
      for (let i = 0; i < this._lumPixels.length; i += 4) s += this._lumPixels[i];
      this._lumMeasured = s / (256 * 255);
    }
    renderer.setRenderTarget(prev);
    this._dispU.uTex.value = rt.texture;
  }

  dispose() {
    if (this._scene && this._display) {
      this._display.onBeforeRender = null; this._scene.remove(this._display);
      this._display.geometry.dispose(); this._display.material.dispose();
    }
    if (this._pyro) { this._pyro.dispose(); this._pyro = null; }
    if (this._shapesQuad) { this._priv.remove(this._shapesQuad); this._shapesQuad.geometry.dispose(); }
    if (this._buffer) this._buffer.dispose();
    if (this._lumTarget) { this._lumTarget.dispose(); this._lumTarget = null; }
    this._scene = null;
  }
}
